import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  List,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { PageHeader } from "../../../shared/components/PageHeader";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import { appNotification } from "../../../shared/notifications/appNotification";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { formatApiTimestamp } from "../../../shared/utils/apiTimestamp";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";
import {
  approveCompanyRegistration,
  fetchCompanyRegistrations,
  rejectCompanyRegistration,
} from "../api/companyRegistrationsApi";
import type {
  CompanyRegistration,
  CompanyRegistrationStatus,
} from "../model/types";
import "../styles/CompanyRegistrations.css";

const { Text } = Typography;
const { useBreakpoint } = Grid;

type CompanyRegistrationStatusFilter = CompanyRegistrationStatus | "ALL";

const statusMeta: Record<
  CompanyRegistrationStatus,
  { label: string; color: string }
> = {
  PENDING: { label: "審査待ち", color: "gold" },
  ACTIVE: { label: "承認済み", color: "green" },
  REJECTED: { label: "却下", color: "red" },
};

const companyLabel = (item: CompanyRegistration) =>
  item.company?.company_out_name ||
  item.company?.company_in_name ||
  item.representative_id;

const applicantLabel = (item: CompanyRegistration) =>
  item.applicant.display_name || item.applicant.email;

export const CompanyRegistrationListPage = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [items, setItems] = useState<CompanyRegistration[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<CompanyRegistrationStatus | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<CompanyRegistration | null>(null);
  const [detailRegistration, setDetailRegistration] = useState<CompanyRegistration | null>(null);
  const [rejectForm] = Form.useForm<{ rejection_reason: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCompanyRegistrations(
        page,
        pageSize,
        status,
        search,
      );
      setItems(result.items);
      setTotal(result.pagination.total);
    } catch (error) {
      const apiError = normalizeApiError(error);
      appNotification.error({
        message: "会社登録申請を取得できませんでした",
        description: apiError.message,
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: load });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, load]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const approve = async (item: CompanyRegistration) => {
    setSavingId(item.representative_user_id);
    try {
      await approveCompanyRegistration(item.representative_user_id);
      appNotification.success({ message: "会社登録申請を承認しました" });
      await load();
    } catch (error) {
      appNotification.error({
        message: "会社登録申請を承認できませんでした",
        description: normalizeApiError(error).message,
      });
    } finally {
      setSavingId(null);
    }
  };

  const openReject = (item: CompanyRegistration) => {
    rejectForm.resetFields();
    setRejecting(item);
  };

  const reject = async () => {
    const values = await rejectForm.validateFields();
    if (!rejecting) return;
    setSavingId(rejecting.representative_user_id);
    try {
      await rejectCompanyRegistration(
        rejecting.representative_user_id,
        values.rejection_reason,
      );
      appNotification.success({ message: "会社登録申請を却下しました" });
      setRejecting(null);
      await load();
    } catch (error) {
      appNotification.error({
        message: "会社登録申請を却下できませんでした",
        description: normalizeApiError(error).message,
      });
    } finally {
      setSavingId(null);
    }
  };

  const columns: ColumnsType<CompanyRegistration> = [
    {
      title: "申請者",
      key: "applicant",
      width: 400,
      render: (_, item) => (
        <Space direction="vertical" size={0}>
          <Text strong>{applicantLabel(item)}</Text>
          <Text type="secondary">{item.applicant.email}</Text>
        </Space>
      ),
    },
    {
      title: "登録会社",
      key: "company",
      width: 400,
      render: (_, item) => (
        <Space direction="vertical" size={0}>
          <Text strong>{companyLabel(item)}</Text>
          <Text type="secondary">{item.representative_id}</Text>
        </Space>
      ),
    },
    {
      title: "状態",
      dataIndex: "status",
      width: 100,
      render: (value: CompanyRegistrationStatus) => (
        <Tag color={statusMeta[value].color}>{statusMeta[value].label}</Tag>
      ),
    },
    {
      title: "申請日時",
      dataIndex: "created_at",
      width: 145,
      render: (value: string) => formatApiTimestamp(value, "datetime"),
    },
    {
      title: "操作",
      key: "actions",
      align: "center",
      minWidth: 300,
      render: (_, item) => (
        <Space wrap size={[8, 8]}>
          <Button icon={<EyeOutlined />} onClick={() => setDetailRegistration(item)}>
            詳細
          </Button>
          {item.status === "PENDING" && (
            <>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={savingId === item.representative_user_id}
              onClick={() => approve(item)}
            >
              承認
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              disabled={savingId === item.representative_user_id}
              onClick={() => openReject(item)}
            >
              却下
            </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="company-registration-page">
      <PageHeader
        title="会社登録申請管理"
        description="顧客が申請した会社登録を確認・審査します。"
        isbutton={false}
      />
      <Card className="company-registration-list-card" bordered={false}>
        <Flex
          className="company-registration-toolbar"
          justify="space-between"
          align="center"
          wrap="wrap"
          gap={12}
        >
          <Input
            className="company-registration-search"
            value={searchInput}
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            placeholder="申請者、メール、会社名、コードで検索"
            style={{ width: 350 }}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Flex className="company-registration-filter-actions" gap={12} wrap="wrap">
            <Select<CompanyRegistrationStatusFilter>
              size="large"
              value={status ?? "ALL"}
              style={{ width: 180 }}
              options={[
                { value: "ALL", label: "すべての状態" },
                ...Object.entries(statusMeta).map(([value, meta]) => ({
                  value,
                  label: meta.label,
                })),
              ]}
              onChange={(value) => {
                setPage(1);
                setStatus(value === "ALL" ? undefined : value);
              }}
            />
            <Button
              className="company-registration-refresh"
              size="large"
              icon={<ReloadOutlined />}
              loading={loading}
              aria-label="更新"
              onClick={load}
            >
              {!isMobile && "更新"}
            </Button>
          </Flex>
        </Flex>
        <div className="company-registration-result-summary" aria-live="polite">
          <Text type="secondary">
            {status ? statusMeta[status].label : "すべての状態"} {total}件
          </Text>
        </div>
        <Table
          className="company-registration-table"
          rowKey="representative_user_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 980 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
        <List
          className="company-registration-mobile-list"
          dataSource={items}
          loading={loading}
          renderItem={(item) => (
            <List.Item
              className="company-registration-mobile-list-item"
              key={item.representative_user_id}
            >
              <Card
                className={`company-registration-mobile-card company-registration-mobile-card--${item.status.toLowerCase()}`}
                size="small"
              >
                <div className="company-registration-mobile-card-header">
                  <div className="company-registration-mobile-company">
                    <Text strong className="company-registration-mobile-company-name">
                      {companyLabel(item)}
                    </Text>
                    <Text className="company-registration-mobile-code">
                      {item.representative_id}
                    </Text>
                  </div>
                  <Tag color={statusMeta[item.status].color}>
                    {statusMeta[item.status].label}
                  </Tag>
                </div>

                <div className="company-registration-mobile-details">
                  <div className="company-registration-mobile-detail">
                    <UserOutlined aria-hidden="true" />
                    <div>
                      <Text className="company-registration-mobile-detail-label">
                        申請者
                      </Text>
                      <Text className="company-registration-mobile-detail-value">
                        {applicantLabel(item)}
                      </Text>
                      <Text className="company-registration-mobile-detail-subvalue">
                        {item.applicant.email}
                      </Text>
                    </div>
                  </div>
                  <div className="company-registration-mobile-detail">
                    <ClockCircleOutlined aria-hidden="true" />
                    <div>
                      <Text className="company-registration-mobile-detail-label">
                        申請日時
                      </Text>
                      <Text className="company-registration-mobile-detail-value">
                        {formatApiTimestamp(item.created_at, "datetime")}
                      </Text>
                    </div>
                  </div>
                </div>

                <div className="company-registration-mobile-actions">
                  <Button
                    className="company-registration-mobile-detail-action"
                    icon={<EyeOutlined />}
                    onClick={() => setDetailRegistration(item)}
                  >
                    詳細を見る
                  </Button>
                  {item.status === "PENDING" && (
                    <>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={savingId === item.representative_user_id}
                      onClick={() => approve(item)}
                    >
                      承認する
                    </Button>
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      disabled={savingId === item.representative_user_id}
                      onClick={() => openReject(item)}
                    >
                      却下する
                    </Button>
                    </>
                  )}
                </div>
              </Card>
            </List.Item>
          )}
        />
        <MobilePagination current={page} pageSize={pageSize} total={total} onChange={setPage} />
      </Card>
      <Modal
        className="company-registration-detail-modal"
        title="会社登録申請の詳細"
        open={Boolean(detailRegistration)}
        footer={
          <Button onClick={() => setDetailRegistration(null)}>
            閉じる
          </Button>
        }
        onCancel={() => setDetailRegistration(null)}
      >
        {detailRegistration && (
          <div className="company-registration-detail-content">
            <div className="company-registration-detail-heading">
              <div>
                <Text type="secondary">登録会社</Text>
                <Text strong>{companyLabel(detailRegistration)}</Text>
                <Text className="company-registration-detail-code">
                  {detailRegistration.representative_id}
                </Text>
              </div>
              <Tag color={statusMeta[detailRegistration.status].color}>
                {statusMeta[detailRegistration.status].label}
              </Tag>
            </div>
            <dl className="company-registration-detail-list">
              <div>
                <dt>申請者</dt>
                <dd>{applicantLabel(detailRegistration)}</dd>
              </div>
              <div>
                <dt>メールアドレス</dt>
                <dd>{detailRegistration.applicant.email}</dd>
              </div>
              <div>
                <dt>申請日時</dt>
                <dd>{formatApiTimestamp(detailRegistration.created_at, "datetime")}</dd>
              </div>
              <div>
                <dt>審査日時</dt>
                <dd>
                  {detailRegistration.reviewed_at
                    ? formatApiTimestamp(detailRegistration.reviewed_at, "datetime")
                    : "未審査"}
                </dd>
              </div>
              <div>
                <dt>審査担当者</dt>
                <dd>
                  {detailRegistration.reviewed_by?.display_name ||
                    detailRegistration.reviewed_by?.email ||
                    "--"}
                </dd>
              </div>
              {detailRegistration.rejection_reason && (
                <div className="company-registration-detail-rejection-reason">
                  <dt>却下理由</dt>
                  <dd>{detailRegistration.rejection_reason}</dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </Modal>
      <Modal
        title="会社登録申請を却下"
        open={Boolean(rejecting)}
        okText="却下する"
        okButtonProps={{
          danger: true,
          loading: savingId === rejecting?.representative_user_id,
        }}
        cancelText="キャンセル"
        onCancel={() => setRejecting(null)}
        onOk={reject}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejection_reason"
            label="却下理由"
            rules={[
              { required: true, whitespace: true, message: "却下理由を入力してください" },
              { max: 1000 },
            ]}
          >
            <Input.TextArea rows={4} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
